import { EDITOR_SIDEBAR_TABS } from "lib/constants";
import EasilIconComponent from "views/components/icons/EasilIcon";
import FullPageLoading from "views/pages/fullLoadingPage/FullPageLoading";
import WindowedPageLoading from "views/pages/fullLoadingPage/WindowedPageLoading";
import OptionDiv from "./sidebar/OptionDiv";
import actionbarStyle from "./actionbar/style.module.css";
import headerStyle from "./header/style.module.css";
import sidebarStyle from "./sidebar/style.module.css";
import editorStyle from "./style.module.css";
import style from "./EditorShell.module.css";

// Duplicated from CanvasBackground / Editor utils: the shell is also the
// Suspense fallback while the editor chunk downloads, so it must not import
// the editor module graph (react-dnd, font/svg loaders) into the main bundle.
const EDITOR_CANVAS_BACKGROUND = "#EDEDEE";
const isEditorInIframe = () => window.parent !== window;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const EasilIcon = EasilIconComponent as any;

const noop = () => {};

interface EditorShellProps {
  isMobile?: boolean;
}

/**
 * Static editor chrome shown while fetchDesign is in flight, so the UI
 * "comes in" instead of popping all at once after a blank spinner.
 *
 * Renders only data-free pieces (header/actionbar frames, the sidebar tab
 * rail, the canvas-area loader) — the real Editor and its connected chrome
 * (EditorSidebarContainer, EditorActionbar) dereference design/designData
 * and crash before the fetch resolves, so none of them can be mounted here.
 */
const EditorShell = ({ isMobile = false }: EditorShellProps) => {
  if (isMobile) {
    // Mobile chrome is a wholly different tree — keep today's behavior.
    return <FullPageLoading backgroundColor={EDITOR_CANVAS_BACKGROUND} />;
  }

  const isInIframe = isEditorInIframe();

  return (
    <div data-testid="EditorShell">
      {/* Same band as the live header, with the logo as an escape hatch back
          home if the design gets stuck loading. Plain anchor (full page nav)
          on purpose — it works even when the app state is wedged. */}
      {!isInIframe && (
        <div className={headerStyle.header}>
          <a href="/" className={headerStyle.header_icon} aria-label="Easil home">
            <EasilIcon color="white" size="32px" />
          </a>
        </div>
      )}
      <div
        className={`${editorStyle.editorBody} translate`}
        data-is-iframe={isInIframe}
      >
        <div
          className={editorStyle.editorBodyActionBarAndCanvas}
          style={{ width: "calc(100% - 384px)" }}
        >
          <div className={actionbarStyle.actionbar} />
          <WindowedPageLoading
            type="vectors"
            backgroundColor={EDITOR_CANVAS_BACKGROUND}
          />
        </div>
        <div className={`${sidebarStyle.sidebar} ${style.staticRail}`}>
          <div className={sidebarStyle.columns}>
            <div className={sidebarStyle.options}>
              {/* Plain native-overflow column (.optionsTop already does
                  flex/overflow). The real sidebar wraps this in Scrollable for
                  a custom scrollbar; the static shell rail never scrolls, so we
                  skip Scrollable's MutationObserver + layout reads + window
                  listeners on the first-paint path. */}
              <div className={sidebarStyle.optionsTop}>
                {EDITOR_SIDEBAR_TABS.map(option => (
                  <OptionDiv
                    key={option.name}
                    option={option}
                    isActive={false}
                    onClick={noop}
                  />
                ))}
              </div>
            </div>
            {/* Empty expansion frame: the editor boots with the side panel
                expanded (sidePanel initialState), so reserve its footprint to
                avoid a 320px rail/canvas jump when the real sidebar mounts. */}
            <div className={sidebarStyle.expansion} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditorShell;
